import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  RotateCcw, 
  Package, 
  Clock, 
  CheckCircle, 
  Shield, 
  ArrowRight,
  ChevronDown,
  HelpCircle,
  AlertTriangle,
  XCircle,
  CreditCard,
  MessageSquare,
  Camera,
  FileText,
  RefreshCw,
  Banknote,
  PackageX,
  PackageCheck,
  AlertCircle,
  Info,
  Scale,
  Sparkles,
  Hammer,
  ThumbsUp,
  ThumbsDown,
  CalendarClock,
  Mail,
  ArrowLeftRight
} from 'lucide-react';

// When you CAN request a return
const CAN_RETURN = [
  {
    title: 'Prodotto non conforme',
    description: 'Il prodotto ricevuto è diverso da quello ordinato o dalla descrizione.',
    icon: PackageX
  },
  {
    title: 'Prodotto difettoso',
    description: 'Il prodotto presenta difetti di fabbricazione o danni non dichiarati.',
    icon: AlertTriangle
  },
  {
    title: 'Prodotto danneggiato in spedizione',
    description: 'Il prodotto è arrivato danneggiato a causa del trasporto.',
    icon: Package
  },
  {
    title: 'Diritto di recesso (14 giorni)',
    description: 'Per prodotti standard acquistati dal catalogo, puoi restituire entro 14 giorni senza motivo.',
    icon: RotateCcw
  }
];

// When you CANNOT request a return
const CANNOT_RETURN = [
  {
    title: 'Prodotti personalizzati o su misura',
    description: 'I prodotti realizzati secondo le tue specifiche indicazioni non possono essere restituiti (Art. 59 Codice del Consumo).',
    icon: Sparkles,
    important: true
  },
  {
    title: 'Prodotti sigillati aperti',
    description: 'Prodotti sigillati per motivi igienici che sono stati aperti dopo la consegna.',
    icon: PackageCheck
  },
  {
    title: 'Beni deteriorabili',
    description: 'Prodotti che rischiano di deteriorarsi o scadere rapidamente.',
    icon: Clock
  },
  {
    title: 'Oltre i 14 giorni',
    description: 'Richieste di reso per ripensamento effettuate oltre i 14 giorni dalla consegna.',
    icon: CalendarClock
  }
];

// Return process steps
const RETURN_STEPS = [
  {
    number: '01',
    title: 'Non confermare la ricezione',
    description: 'Se noti un problema, NON confermare la ricezione nella tua dashboard. Il pagamento all\'artigiano resta trattenuto.',
    icon: AlertCircle,
    color: 'bg-red-500'
  },
  {
    number: '02',
    title: 'Contatta l\'artigiano',
    description: 'Scrivi all\'artigiano tramite la chat della piattaforma, spiegando il problema e allegando foto se necessario.',
    icon: MessageSquare,
    color: 'bg-blue-500'
  },
  {
    number: '03',
    title: 'Trova un accordo',
    description: 'Nella maggior parte dei casi, artigiano e cliente trovano una soluzione: sostituzione, riparazione o rimborso.',
    icon: ThumbsUp,
    color: 'bg-green-500'
  },
  {
    number: '04',
    title: 'Assistenza Rarifex',
    description: 'Se non trovate un accordo, il team Rarifex interviene come mediatore per risolvere la controversia.',
    icon: Shield,
    color: 'bg-purple-500'
  }
];

// Refund timeline
const REFUND_TIMELINE = [
  {
    phase: 'Richiesta accettata',
    timing: 'Immediato',
    description: 'Il rimborso viene avviato non appena la richiesta è approvata'
  },
  {
    phase: 'Elaborazione Stripe',
    timing: '1-3 giorni',
    description: 'Stripe elabora la transazione di rimborso'
  },
  {
    phase: 'Riaccredito carta',
    timing: '5-10 giorni',
    description: 'La tua banca riaccredita l\'importo sulla carta (tempi variabili per banca)'
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: 'Posso restituire un prodotto personalizzato?',
    answer: 'No, i prodotti realizzati su misura o personalizzati secondo le tue indicazioni non possono essere restituiti per ripensamento. Questo è previsto dall\'Art. 59 del Codice del Consumo. Tuttavia, se il prodotto è difettoso o non conforme a quanto concordato, hai comunque diritto alla sostituzione o al rimborso.'
  },
  {
    question: 'Entro quanto tempo posso chiedere il reso?',
    answer: 'Per i prodotti standard (non personalizzati), hai 14 giorni dalla ricezione per esercitare il diritto di recesso senza dover fornire motivazioni. Per segnalare difetti o non conformità, hai tempo fino a quando non confermi la ricezione (massimo 7 giorni dalla consegna).'
  },
  {
    question: 'Chi paga le spese di spedizione per il reso?',
    answer: 'Dipende dal motivo del reso. Se il prodotto è difettoso o non conforme, le spese sono a carico dell\'artigiano. Se eserciti il diritto di recesso per ripensamento (prodotto conforme), le spese di reso sono generalmente a tuo carico, salvo diversi accordi con l\'artigiano.'
  },
  {
    question: 'Quanto tempo ci vuole per ricevere il rimborso?',
    answer: 'Una volta approvato il rimborso, Stripe lo elabora in 1-3 giorni lavorativi. La tua banca poi riaccredita l\'importo sulla carta in 5-10 giorni lavorativi. I tempi totali sono quindi di circa 1-2 settimane, ma possono variare in base alla tua banca.'
  },
  {
    question: 'Cosa faccio se il pacco arriva danneggiato?',
    answer: 'Fotografa immediatamente l\'imballaggio e il prodotto danneggiato, possibilmente prima di accettare la consegna o subito dopo. Non confermare la ricezione nella dashboard. Contatta l\'artigiano tramite la chat allegando le foto. In caso di danni evidenti da trasporto, segnala anche al corriere.'
  },
  {
    question: 'Posso cambiare un prodotto con un altro?',
    answer: 'Gli scambi diretti non sono previsti dalla piattaforma. Puoi richiedere il reso e il rimborso del prodotto originale, e poi effettuare un nuovo ordine per il prodotto desiderato. Alcuni artigiani potrebbero essere disponibili a gestire scambi diretti: contattali via chat per verificare.'
  },
  {
    question: 'E se l\'artigiano non risponde?',
    answer: 'Se l\'artigiano non risponde entro 48 ore, contatta il supporto Rarifex. Interveniamo come mediatori e, se necessario, possiamo procedere al rimborso diretto trattenendo i fondi che non sono ancora stati rilasciati all\'artigiano.'
  },
  {
    question: 'Come funziona la garanzia legale?',
    answer: 'Tutti i prodotti sono coperti dalla garanzia legale di 2 anni per difetti di conformità (Art. 128-135 Codice del Consumo). Se un difetto si manifesta entro 2 anni dalla consegna, puoi richiedere la riparazione o sostituzione gratuita, e se non possibile, la riduzione del prezzo o il rimborso.'
  },
  {
    question: 'Cosa succede se ho già confermato la ricezione?',
    answer: 'Se hai già confermato la ricezione, il pagamento è stato rilasciato all\'artigiano. Puoi comunque contattare l\'artigiano per segnalare il problema e cercare una soluzione. Per difetti non evidenti scoperti successivamente, la garanzia legale ti tutela per 2 anni.'
  },
  {
    question: 'Posso annullare un ordine prima della spedizione?',
    answer: 'Per i prodotti standard non ancora spediti, puoi richiedere l\'annullamento contattando l\'artigiano. Per i prodotti personalizzati, una volta iniziata la lavorazione l\'annullamento potrebbe non essere possibile o comportare un rimborso parziale per i materiali già utilizzati.'
  }
];

export default function ReturnsPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Resi e Rimborsi su Rarifex | Politica Reso | Diritto di Recesso</title>
        <meta name="description" content="Scopri la politica di resi e rimborsi su Rarifex. Diritto di recesso 14 giorni, rimborsi protetti con Stripe, garanzia legale 2 anni. Procedura semplice e trasparente." />
        <meta name="keywords" content="resi rarifex, rimborsi rarifex, politica reso, diritto recesso, garanzia legale, restituire prodotto, rimborso stripe" />
        <link rel="canonical" href="https://www.rarifex.com/resi-rimborsi" />
        <meta property="og:title" content="Resi e Rimborsi su Rarifex | Politica Reso" />
        <meta property="og:description" content="Politica resi trasparente. Diritto di recesso 14 giorni per prodotti standard, rimborsi rapidi e sicuri con Stripe." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/resi-rimborsi" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-teal-500/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <RotateCcw className="w-4 h-4" />
                Resi e Rimborsi
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Acquisti protetti,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
                  sempre
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Se qualcosa non va, siamo qui per aiutarti. 
                <strong className="text-slate-800"> Rimborsi rapidi, procedura semplice, assistenza dedicata.</strong>
              </p>

              {/* Key points */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-10">
                <div className="flex items-center gap-2 text-slate-700">
                  <CalendarClock className="w-5 h-5 text-teal-500" />
                  <span className="text-sm font-medium">14 giorni per ripensarci</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Shield className="w-5 h-5 text-teal-500" />
                  <span className="text-sm font-medium">Pagamento Protetto</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Scale className="w-5 h-5 text-teal-500" />
                  <span className="text-sm font-medium">Garanzia Legale 2 anni</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHEN YOU CAN RETURN */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12">
                
                {/* CAN return */}
                <div>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Quando puoi restituire</h2>
                      <p className="text-slate-600">Casi in cui hai diritto al reso o rimborso</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {CAN_RETURN.map((item, index) => (
                      <Card key={index} className="p-4 bg-green-50/50 border-green-200/50">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{item.title}</h3>
                            <p className="text-sm text-slate-600">{item.description}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* CANNOT return */}
                <div>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Quando non puoi restituire</h2>
                      <p className="text-slate-600">Eccezioni previste dalla legge</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {CANNOT_RETURN.map((item, index) => (
                      <Card key={index} className={`p-4 ${item.important ? 'bg-amber-50 border-amber-300' : 'bg-red-50/50 border-red-200/50'}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.important ? 'bg-amber-100' : 'bg-red-100'}`}>
                            <item.icon className={`w-5 h-5 ${item.important ? 'text-amber-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{item.title}</h3>
                            <p className="text-sm text-slate-600">{item.description}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* IMPORTANT NOTICE - PERSONALIZED PRODUCTS */}
        <section className="py-12 md:py-16 bg-amber-50 border-y border-amber-200">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Prodotti personalizzati e su misura
                  </h3>
                  <p className="text-slate-700 mb-4">
                    Ai sensi dell'<strong>Art. 59 del Codice del Consumo</strong>, il diritto di recesso 
                    non si applica ai prodotti confezionati su misura o chiaramente personalizzati. 
                    Questo include tutti i prodotti realizzati secondo le tue specifiche indicazioni.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Prima di confermare l'acquisto di un prodotto personalizzato, ti viene richiesto 
                    di accettare esplicitamente questa condizione. La garanzia legale per difetti di 
                    conformità resta comunque attiva.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RETURN PROCESS */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <ArrowLeftRight className="w-4 h-4" />
                  Procedura
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Come richiedere un reso o rimborso
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Segui questi passaggi per risolvere qualsiasi problema con il tuo ordine.
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-6">
                {RETURN_STEPS.map((step, index) => (
                  <div key={index} className="relative">
                    {index < RETURN_STEPS.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2 z-0" />
                    )}
                    <Card className="p-6 bg-white border-slate-200 hover:shadow-lg transition-all relative z-10 h-full">
                      <div className={`w-12 h-12 ${step.color} rounded-xl flex items-center justify-center mb-4`}>
                        <step.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-xs font-bold text-slate-400 mb-2">{step.number}</div>
                      <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-600">{step.description}</p>
                    </Card>
                  </div>
                ))}
              </div>

              {/* Key tip */}
              <div className="mt-10 bg-red-50 border border-red-200 rounded-2xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">Importante: Non confermare la ricezione!</h3>
                    <p className="text-slate-700">
                      Se noti un problema con il prodotto, <strong>non confermare la ricezione</strong> nella tua dashboard. 
                      Finché non confermi, il pagamento resta trattenuto da Stripe e hai maggiore potere negoziale. 
                      Una volta confermata la ricezione, il pagamento viene rilasciato all'artigiano.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* REFUND TIMELINE */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Tempistiche del rimborso
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                  Una volta approvato il rimborso, ecco quanto tempo ci vuole.
                </p>
              </div>

              <div className="bg-slate-800/50 rounded-2xl p-8">
                <div className="space-y-6">
                  {REFUND_TIMELINE.map((item, index) => (
                    <div key={index} className="flex items-center gap-6">
                      <div className="w-24 md:w-32 flex-shrink-0">
                        <span className="text-teal-400 font-bold">{item.timing}</span>
                      </div>
                      <div className="w-4 h-4 bg-teal-400 rounded-full flex-shrink-0"></div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{item.phase}</h4>
                        <p className="text-sm text-slate-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-slate-700">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-slate-400" />
                    <p className="text-slate-400 text-sm">
                      Il rimborso viene riaccreditato sulla stessa carta usata per il pagamento originale.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LEGAL GUARANTEE */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Scale className="w-4 h-4" />
                    Garanzia Legale
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    2 anni di garanzia su tutti i prodotti
                  </h2>
                  <p className="text-lg text-slate-600 mb-6">
                    Oltre al diritto di recesso, tutti i prodotti acquistati su Rarifex sono coperti 
                    dalla <strong>garanzia legale di conformità</strong> prevista dal Codice del Consumo 
                    (Art. 128-135).
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-slate-900">Durata 2 anni</h4>
                        <p className="text-sm text-slate-600">La garanzia copre i difetti di conformità per 24 mesi dalla consegna</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-slate-900">Riparazione o sostituzione</h4>
                        <p className="text-sm text-slate-600">Puoi chiedere la riparazione o sostituzione gratuita del prodotto difettoso</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-slate-900">Rimborso se non riparabile</h4>
                        <p className="text-sm text-slate-600">Se riparazione e sostituzione sono impossibili, hai diritto al rimborso</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visual card */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-100">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Shield className="w-10 h-10 text-purple-600" />
                    </div>
                    <h3 className="text-5xl font-bold text-purple-600 mb-2">2 anni</h3>
                    <p className="text-lg text-slate-700 mb-6">di garanzia legale</p>
                    
                    <div className="text-left bg-white rounded-xl p-4 border border-purple-100">
                      <p className="text-sm text-slate-600">
                        <strong className="text-slate-900">Nota:</strong> La garanzia copre i difetti di conformità 
                        presenti al momento della consegna, non i danni causati da uso improprio, usura normale 
                        o manutenzione inadeguata.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT SUPPORT */}
        <section className="py-12 md:py-16 bg-teal-50 border-y border-teal-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <MessageSquare className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Hai bisogno di aiuto?
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Se non riesci a risolvere il problema con l'artigiano o hai domande sulla procedura di reso, 
                il nostro team di supporto è a tua disposizione.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                    <Mail className="w-4 h-4 mr-2" />
                    Contatta il Supporto
                  </Button>
                </Link>
                <Link to="/faq">
                  <Button variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-100">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Leggi le FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <HelpCircle className="w-4 h-4" />
                  Domande Frequenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande su resi e rimborsi
                </h2>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-teal-500 to-cyan-500">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Shield className="w-12 h-12 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Acquista senza pensieri
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Con i pagamenti protetti e la nostra politica di reso trasparente, 
                ogni acquisto su Rarifex è un'esperienza sicura.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-white text-teal-600 hover:bg-slate-100">
                    <Package className="w-5 h-5 mr-2" />
                    Esplora i Prodotti
                  </Button>
                </Link>
                <Link to="/pagamenti">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Come Funzionano i Pagamenti
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
